/* The additional warranty plan (snippets/oracura-warranty-buy.liquid). Add To Bag stays locked
   until the warranty form on /pages/additional-warranty has been sent from this device.

   The form is the Acerill form app's. After a submission it calls
   window.acerillFormBuilderOnFormSubmitted(data) and then moves the shopper to
   /pages/warranty-main-page. That call is where the form is recorded here, for a day, with the
   product and serial number given; they travel with the plan as line properties. A plan chosen
   before the form is remembered for the visit, and the thank-you page adds it.

   This is a step on the page, not a lock on the cart: a request made straight to /cart/add.js is
   not stopped by it. */

import { addItems } from '@theme/oracura-cart';

const FORM_ID = '8f9d1515-19e6-48e2-82de-4900d5fb8b90';
const FIELDS = { product: 'f4351454-d88a-45cb-a7d7-1dfa373f5102', serial: 'fd856930-1a8b-4763-8767-a38ae49c1aa9' };
const FORM_KEY = 'oracura:warranty-form';
const PLAN_KEY = 'oracura:warranty-plan';
const DAY = 24 * 60 * 60 * 1000;

// Storage can be missing or refuse (a private window, blocked site data): then nothing is
// remembered, and the shopper is asked for the form again.
function read(store, key) {
  try { return JSON.parse(window[store].getItem(key) || 'null'); } catch (error) { return null; }
}
function write(store, key, value) {
  try {
    if (value == null) window[store].removeItem(key);
    else window[store].setItem(key, JSON.stringify(value));
  } catch (error) { /* not remembered */ }
}

function sent() {
  const form = read('localStorage', FORM_KEY);
  return form && Date.now() - form.at < DAY ? form : null;
}

function text(field) {
  if (field == null) return '';
  if (Array.isArray(field)) return field.map(text).join(', ');
  if (typeof field === 'object') return text(field.value ?? field.label ?? '');
  return String(field).trim().slice(0, 120);
}

function record(data) {
  if (data?.formId && data.formId !== FORM_ID) return;
  write('localStorage', FORM_KEY, { at: Date.now(), product: text(data?.[FIELDS.product]), serial: text(data?.[FIELDS.serial]) });
  document.dispatchEvent(new CustomEvent('oracura:warranty-form'));
}

// The form's own custom JavaScript may set this hook as well; when it does, both run.
if (!window.OracuraWarrantyHook) {
  let theirs = typeof window.acerillFormBuilderOnFormSubmitted === 'function' ? window.acerillFormBuilderOnFormSubmitted : null;
  const ours = function (data) {
    try { record(data); } catch (error) { /* the form still goes through */ }
    if (theirs) return theirs.apply(this, arguments);
  };
  window.OracuraWarrantyHook = ours;
  try {
    Object.defineProperty(window, 'acerillFormBuilderOnFormSubmitted', {
      configurable: true,
      get: () => ours,
      set: (fn) => { if (typeof fn === 'function' && fn !== ours) theirs = fn; },
    });
  } catch (error) {
    window.acerillFormBuilderOnFormSubmitted = ours;
  }
}

// GoKwik opens its cart on every add request it sees, refused ones included; after a refused
// add it is closed again so the message is what the shopper sees (as oracura-ui.js).
function closeCartAfterFailedAdd() {
  let tries = 0;
  const check = () => {
    const open = document.querySelector('shadow-dom-container')?.shadowRoot?.querySelector('.sidecart');
    if (open && typeof window.closeGokwikSideCart === 'function') { window.closeGokwikSideCart(); return; }
    if (++tries < 10) setTimeout(check, 150);
  };
  check();
}

class OracuraWarrantyBuy extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.button = this.querySelector('[data-wbuy-add]');
    this.note = this.querySelector('[data-wbuy-note]');
    this.addEventListener('change', (e) => { if (e.target.name === 'warrantyOption') this.show(); });
    this.button?.addEventListener('click', () => this.press());
    document.addEventListener('oracura:warranty-form', () => { this.said = false; this.sync(); });
    const plan = read('sessionStorage', PLAN_KEY);
    const remembered = plan && this.input(plan.variant);
    if (remembered && !remembered.disabled) remembered.checked = true;
    this.show();
    if (this.dataset.mode === 'thanks') {
      // The form app sends the shopper here straight after a submission. Should its hook not
      // have run, arriving from the form page is taken as the submission.
      if (!sent() && /\/pages\/additional-warranty/.test(document.referrer)) record({});
      if (document.readyState === 'complete') this.finish();
      else window.addEventListener('load', () => this.finish(), { once: true });
    }
    this.sync();
  }

  input(id) {
    return [...this.querySelectorAll('input[name="warrantyOption"]')].find((input) => input.dataset.variant === String(id));
  }

  chosen() { return this.querySelector('input[name="warrantyOption"]:checked'); }

  show() {
    const input = this.chosen();
    if (!input) return;
    const price = this.querySelector('[data-wbuy-price]');
    if (price) price.textContent = input.dataset.price || '';
    const image = this.querySelector('[data-wbuy-image]');
    if (image && input.dataset.image) image.src = input.dataset.image;
  }

  sync() {
    const open = Boolean(sent());
    this.button?.toggleAttribute('data-locked', !open);
    if (open && this.note && !this.said) {
      this.note.textContent = this.dataset.mode === 'thanks'
        ? 'Thank you, we have received your warranty form. Choose your plan and add it to your bag.'
        : 'Warranty form received. You can now add your plan to the bag.';
    }
  }

  say(message) {
    if (!this.note) return;
    this.said = true;
    this.note.textContent = message;
  }

  press() {
    const input = this.chosen();
    if (!input || this.busy) return;
    if (sent()) { this.add(input); return; }
    write('sessionStorage', PLAN_KEY, { variant: input.dataset.variant });
    const formUrl = this.dataset.formUrl || '#warranty-form';
    if (!formUrl.startsWith('#')) { location.assign(formUrl); return; }
    this.say(`Please fill in the warranty form below first. Your ${input.value} plan goes in the bag as soon as it has been sent.`);
    const form = document.querySelector(formUrl);
    if (form) {
      form.scrollIntoView({ behavior: 'smooth', block: 'start' });
      form.focus({ preventScroll: true });
    }
  }

  finish() {
    const plan = read('sessionStorage', PLAN_KEY);
    const input = plan && sent() && this.input(plan.variant);
    if (!input || input.disabled) return;
    input.checked = true;
    this.show();
    this.add(input);
  }

  async add(input) {
    const form = sent();
    const properties = { '_Warranty form': 'Submitted' };
    if (form?.product) properties['For product'] = form.product;
    if (form?.serial) properties['Serial number'] = form.serial;
    this.busy = true;
    this.button.setAttribute('aria-busy', 'true');
    try {
      await addItems([{ id: Number(input.dataset.variant), quantity: 1, properties }], this);
      write('sessionStorage', PLAN_KEY, null);
      this.say(`${input.value} added to your bag.`);
    } catch (error) {
      closeCartAfterFailedAdd();
      this.say(error.message);
    } finally {
      this.busy = false;
      this.button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('oracura-warranty-buy')) customElements.define('oracura-warranty-buy', OracuraWarrantyBuy);
